import { z } from 'zod';
import { FinancialLineStatusSchema, TariffBasisSchema } from '../enums/finance.js';
import { IsoDateStringSchema } from './common.js';
import { MoneyStringSchema, MyNetworkQuerySchema, TariffNoteSchema } from './finance.js';

/**
 * Commissions vendeurs (lot 5) : ce que le RÉSEAU paie à SES vendeurs.
 *
 *  - la grille appartient au RP du réseau (flux `vendor_commission` de la
 *    table `tariffs`, `partnerId` OBLIGATOIRE) ; Hipto ne la paie pas et ne
 *    l'additionne jamais au CA, à la rétrocession ni à la marge ;
 *  - le RP décide qui, dans son réseau, voit quoi (`FinanceVisibilitySettings`,
 *    tout à `false` par défaut) ;
 *  - personne sous le RP ne voit jamais le CA, la rétrocession (sauf le
 *    back-office si le RP l'ouvre) ni la marge.
 *
 * Tout reste derrière `features.financeVisibility` (403 pour tout non-admin
 * tant qu'il vaut `admin_only`) et aucune de ces routes n'est ouverte à une
 * session PIN.
 */

const RefIdSchema = z.string().min(1).max(64);
const TariffAmountSchema = MoneyStringSchema.refine(
  (v) => !v.startsWith('-'),
  'Montant négatif interdit',
);
const MonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mois attendu au format AAAA-MM');

// ── Réglages de visibilité (par réseau, possédés par le RP) ──────────

/**
 * Qui voit quoi dans le réseau. Défaut : tout à `false` — seul le RP voit.
 *
 *  - `backofficeSeesPayout` : le responsable back-office voit la
 *    rétrocession du réseau (lecture seule, mêmes écrans que le RP) ;
 *  - `backofficeSeesVendorCommissions` : il voit les commissions de tous
 *    les vendeurs du réseau ;
 *  - `managersSeeTeamCommissions` : managers et superviseurs voient les
 *    commissions de LEUR hiérarchie (`VisibilityService.leads`) ;
 *  - `vendorsSeeOwnCommission` : chaque vendeur voit SA commission, jamais
 *    celle d'un autre.
 */
export const FINANCE_VISIBILITY_SETTING_KEYS = [
  'backofficeSeesPayout',
  'backofficeSeesVendorCommissions',
  'managersSeeTeamCommissions',
  'vendorsSeeOwnCommission',
] as const;
export type FinanceVisibilitySettingKey = (typeof FINANCE_VISIBILITY_SETTING_KEYS)[number];

export const FinanceVisibilitySettingsSchema = z.object({
  partnerId: z.string(),
  partnerName: z.string(),
  backofficeSeesPayout: z.boolean(),
  backofficeSeesVendorCommissions: z.boolean(),
  managersSeeTeamCommissions: z.boolean(),
  vendorsSeeOwnCommission: z.boolean(),
});
export type FinanceVisibilitySettings = z.infer<typeof FinanceVisibilitySettingsSchema>;

/** `GET /finance/visibility-settings` : le RP lit SON réseau ; l'admin choisit le réseau. */
export const FinanceVisibilitySettingsQuerySchema = z.object({
  partnerId: RefIdSchema.optional(),
});
export type FinanceVisibilitySettingsQuery = z.infer<typeof FinanceVisibilitySettingsQuerySchema>;

/** `PATCH /finance/visibility-settings` (RP seul) : au moins un réglage. */
export const UpdateFinanceVisibilitySettingsSchema = z
  .object({
    backofficeSeesPayout: z.boolean().optional(),
    backofficeSeesVendorCommissions: z.boolean().optional(),
    managersSeeTeamCommissions: z.boolean().optional(),
    vendorsSeeOwnCommission: z.boolean().optional(),
  })
  .strict()
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Aucun réglage à modifier');
export type UpdateFinanceVisibilitySettingsInput = z.infer<
  typeof UpdateFinanceVisibilitySettingsSchema
>;

// ── Ce que la personne connectée peut voir (navigation) ──────────────

/**
 * `GET /finance/access` : ce que l'API ouvrira à la personne connectée —
 * sert à l'UI pour n'afficher que des portes ouvertes. Aucune donnée, aucun
 * montant ; l'API reste la garde (chaque route revérifie).
 *
 *  - `payout` : `network` = RP (sa rétrocession), `read` = back-office
 *    autorisé (lecture seule), `none` sinon ;
 *  - `vendorCommissions` : périmètre des commissions vendeurs visibles ;
 *  - `vendorGrid` / `settings` : grille et réglages du réseau — `edit` pour
 *    le RP, `view` pour l'admin.
 */
export const VENDOR_COMMISSION_SCOPES = ['none', 'own', 'team', 'network', 'all'] as const;
export type VendorCommissionScope = (typeof VENDOR_COMMISSION_SCOPES)[number];

export const MyFinanceAccessSchema = z.object({
  payout: z.enum(['none', 'network', 'read']),
  vendorCommissions: z.enum(VENDOR_COMMISSION_SCOPES),
  vendorGrid: z.enum(['none', 'edit', 'view']),
  settings: z.enum(['none', 'edit', 'view']),
});
export type MyFinanceAccess = z.infer<typeof MyFinanceAccessSchema>;

// ── Grille des commissions vendeurs ──────────────────────────────────

/**
 * Nouvelle version de grille de commission vendeur. Le réseau est
 * OBLIGATOIRE (pas de ligne par défaut entre réseaux) et doit appartenir au
 * périmètre d'écriture du RP — son propre réseau.
 */
export const CreateVendorCommissionTariffSchema = z
  .object({
    partnerId: RefIdSchema,
    verticaleId: RefIdSchema,
    offerKey: z.string().trim().min(1).max(200).nullable().optional(),
    basis: TariffBasisSchema,
    amount: TariffAmountSchema,
    validFrom: IsoDateStringSchema,
    validTo: IsoDateStringSchema.nullable().optional(),
    note: TariffNoteSchema,
  })
  .superRefine((v, ctx) => {
    if (v.validTo && new Date(v.validTo) <= new Date(v.validFrom)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['validTo'],
        message: 'La fin de validité doit suivre le début',
      });
    }
  });
export type CreateVendorCommissionTariffInput = z.infer<typeof CreateVendorCommissionTariffSchema>;

export const VendorCommissionTariffListQuerySchema = z.object({
  partnerId: RefIdSchema.optional(),
  verticaleId: RefIdSchema.optional(),
});
export type VendorCommissionTariffListQuery = z.infer<typeof VendorCommissionTariffListQuerySchema>;

// ── Commissions (réseau, équipe, admin) ──────────────────────────────

export const VendorCommissionQuerySchema = MyNetworkQuerySchema.extend({
  /** Filtre réseau (admin) ; pour les autres, intersecté avec leur périmètre. */
  partnerId: RefIdSchema.optional(),
});
export type VendorCommissionQuery = z.infer<typeof VendorCommissionQuerySchema>;

export const VendorCommissionLinesQuerySchema = VendorCommissionQuerySchema.extend({
  /** Matricule d'un vendeur (dépliage d'une ligne « par vendeur »). */
  matricule: z.string().trim().min(1).max(32).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type VendorCommissionLinesQuery = z.infer<typeof VendorCommissionLinesQuerySchema>;

export const VendorCommissionSummaryRowSchema = z.object({
  /** Mois `YYYY-MM`, id de réseau, ou matricule du vendeur. */
  key: z.string(),
  label: z.string(),
  earned: z.string(),
  estimated: z.string(),
  leadCount: z.number().int(),
});
export type VendorCommissionSummaryRow = z.infer<typeof VendorCommissionSummaryRowSchema>;

export const VendorCommissionSummarySchema = z.object({
  byMonth: z.array(VendorCommissionSummaryRowSchema),
  byPartner: z.array(VendorCommissionSummaryRowSchema),
  /** Par matricule — jamais le nom du vendeur. */
  byVendor: z.array(VendorCommissionSummaryRowSchema),
  total: VendorCommissionSummaryRowSchema,
});
export type VendorCommissionSummary = z.infer<typeof VendorCommissionSummarySchema>;

/** Ligne de commission vendeur. Rien sur l'acheteur, le CA ni la rétrocession. */
export const VendorCommissionLineSchema = z.object({
  id: z.string(),
  leadId: z.string(),
  leadCreatedAt: IsoDateStringSchema,
  partnerId: z.string(),
  partnerName: z.string(),
  verticaleName: z.string().nullable(),
  vendorMatricule: z.string().nullable(),
  amount: z.string(),
  status: FinancialLineStatusSchema,
  reason: z.string(),
  createdAt: IsoDateStringSchema,
});
export type VendorCommissionLine = z.infer<typeof VendorCommissionLineSchema>;

export const VendorCommissionLinesSchema = z.object({
  items: z.array(VendorCommissionLineSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type VendorCommissionLines = z.infer<typeof VendorCommissionLinesSchema>;

// ── Mes commissions (vendeur) ────────────────────────────────────────

export const MyCommissionsQuerySchema = z.object({ month: MonthSchema });
export type MyCommissionsQuery = z.infer<typeof MyCommissionsQuerySchema>;

/** Une fiche du vendeur et ce qu'elle lui rapporte (net). */
export const MyCommissionLeadSchema = z.object({
  leadId: z.string(),
  leadCreatedAt: IsoDateStringSchema,
  verticaleName: z.string().nullable(),
  earned: z.string(),
  estimated: z.string(),
});
export type MyCommissionLead = z.infer<typeof MyCommissionLeadSchema>;

export const MyCommissionsSchema = z.object({
  month: z.string(),
  earned: z.string(),
  estimated: z.string(),
  leads: z.array(MyCommissionLeadSchema),
});
export type MyCommissions = z.infer<typeof MyCommissionsSchema>;

/** Commission d'UNE fiche du vendeur (encart de la fiche). */
export const MyLeadCommissionSchema = z.object({
  leadId: z.string(),
  earned: z.string(),
  estimated: z.string(),
});
export type MyLeadCommission = z.infer<typeof MyLeadCommissionSchema>;
